import React, { useState, useEffect, useCallback } from 'react';
import { Field, reduxForm, formValueSelector, change, untouch } from 'redux-form';
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import moment from 'moment';

import ConfirmationTextBox from "../../../components/ConfirmationPrompt";
import { addClass, removeClass, isMobile } from '../../../common/util';
import Icon from '../../../icons';
import { MAX_UPLOAD_FILE_SIZE, INVALID_FILES_TO_UPLOAD } from '../../../containers/WorkkardDetail/constants';
import { showTooltip, hideTooltip } from "../../../containers/WorkkardDetail/actions/action";
import constant from "./constants";
import MyCalendar from "../../../components/MyCalendar";
import FormTextInput from '../../../components/FormComponents/formTextInput';
import FormTextEditor from '../../../components/FormComponents/formTextEditor';
import FormDatePicker from '../../../components/FormComponents/formDatePicker';
import FormSelect from "../../../components/FormComponents/formSelect";
import FormTags from "../../../components/FormComponents/formTags";
import FormDiaryOwner from "../../../components/FormComponents/formDiaryOwner";
import FormFileUpload from "../../../components/FormComponents/formFileUpload";
import LoaderComponent from '../../../components/Loader';
import { validate } from "./validate";

// Constants
const FORM_NAME = constant.FORM_NAME;
const selector = formValueSelector(FORM_NAME);

const DEFAULT_REMINDER_TIME = () =>
    moment().set({ hour: 23, minute: 59, second: 0 });

// Helpers
const onSubmitFail = () => {
    document.querySelector(".ReactModal__Overlay")?.scrollTo(0, 0);
};

// Resize the modal between 'small' and 'medium'
const setModalSize = (size) => {
    const modal = document.querySelector(".ReactModal__Content");
    if (!modal) return;
    const opposite = size === "small" ? "medium" : "small";
    removeClass(modal, opposite);
    addClass(modal, size);
};

// Sum Component
const CancelConfirmModal = ({ onConfirm, onCancel }) => (
    <ConfirmationTextBox
        class="modalBody"
        message="Your changes have not been saved, are you sure you want to cancel?"
        onConfirmClick={onConfirm}
        onCancelClick={onCancel}
        confirmationText="Confirm"
        footerClass="modalFooter"
        cancelText="Cancel"
    />
);

const FormFooter = ({ buttonLoading, isEdit, onSave, onFollowUp, onCancel }) => (
    <div className="rowFlex alignRight alignMiddle mt">
        <button
            className="btn buttonGradient"
            disabled={buttonLoading}
            type="submit"
            onClick={onSave}
        >
            {buttonLoading ? <LoaderComponent /> : <span>Save</span>}
        </button>

        {!isEdit && constant.CAN_ADD_MORE_DUE_DATES && (
            <button
                disabled={buttonLoading}
                className="btn buttonGradient"
                type="submit"
                onClick={onFollowUp}
            >
                {buttonLoading ? <LoaderComponent /> : <span>Save and add Follow Up</span>}
            </button>
        )}

        <button className="btn link" type="button" onClick={onCancel}>
            Cancel
        </button>
    </div>
);

const DiaryCreationForm = ({
    handleSubmit,
    buttonLoading,
    initialValues,
    isEdit,
    userId,
    isDirty,
    toggleFullView,
    closeForm,
    currentPage,
    systemTags,
    tags,
    onSubmit,
}) => {
    const dispatch = useDispatch();
    const [submitType, setSubmitType] = useState("save");
    const [showModal, setShowModal] = useState(false);

    const {
        dueDate, isReminderDisabled, ownerId, isDateComment,
        timezoneData, isLoading, userTimeZone, currentDueDateTz, currentReminderTz,
    } = useSelector(state => ({
        dueDate:            selector(state, 'DateTime'),
        isReminderDisabled: !selector(state, 'enableReminder'),
        ownerId:            selector(state, 'owner'),
        isDateComment:      selector(state, 'isDateComment'),
        timezoneData:       state.diaryCreation.timezoneData || [],
        isLoading:          state.diaryCreation.loadingOwners,
        userTimeZone:       state?.sessionContext?.TimeZoneTz,
        currentDueDateTz:   selector(state, 'due_on_tz'),
        currentReminderTz:  selector(state, 'reminder_setting_tz'),
    }), shallowEqual);

    // Sync timezones on load
    useEffect(() => {
        if (!timezoneData.length) return;
        if (initialValues.due_on_tz === null && !currentDueDateTz) {
            dispatch(change(FORM_NAME, 'due_on_tz', userTimeZone));
        }
        if (initialValues.reminder_setting_tz === null && !currentReminderTz) {
            dispatch(change(FORM_NAME, 'reminder_setting_tz', userTimeZone));
        }
    }, [timezoneData, userTimeZone, dispatch, initialValues.due_on_tz, initialValues.reminder_setting_tz, currentDueDateTz, currentReminderTz]);

    // Esc key handler
    const handleCancelClick = useCallback(() => {
        setModalSize("small");
        if (isDirty) {
            setShowModal(true);
            toggleFullView(false);
        } else {
            closeForm();
        }
    }, [isDirty, toggleFullView, closeForm]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isDirty) {
                e.preventDefault();
                e.stopPropagation();
                handleCancelClick();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isDirty, handleCancelClick]);

    // Date comment helper
    const syncDateComment = useCallback((date) => {
        if (isEdit) {
            dispatch(change(FORM_NAME, "isDateComment", !moment(initialValues.DateTime).isSame(date)));
        }
    }, [isEdit, initialValues.DateTime, dispatch]);

    // Field change handler
    const onTxtDaysChange = useCallback((event) => {
        const raw = event.target.value;
        if (isNaN(raw)) { event.preventDefault(); return; }

        const days = parseInt(raw, 10);

        if (days > 999) {
            dispatch(change(FORM_NAME, "txtDays", '999+'));
            event.preventDefault();
            return;
        }

        if (days > 0) {
            const newDate = moment().add(days, 'days').set({ hour: 23, minute: 59, second: 0 });
            dispatch(change(FORM_NAME, "DateTime", newDate));
            syncDateComment(newDate);
        } else {
            dispatch(change(FORM_NAME, "DateTime", initialValues.DateTime));
            syncDateComment(initialValues.DateTime);
        }
    }, [dispatch, initialValues.DateTime, syncDateComment]);

    const onTxtDaysFocus = useCallback((e) => {
        if (!e.target.value.includes('+')) return;
        dispatch(change(FORM_NAME, "txtDays", null));
        dispatch(change(FORM_NAME, "DateTime", initialValues.DateTime));
        syncDateComment(initialValues.DateTime);
    }, [dispatch, initialValues.DateTime, syncDateComment]);

    const onDateTimeChange = useCallback((_, value) => {
        syncDateComment(value);
        const daysDiff = value?.diff(moment().startOf('day'), "days");
        if (daysDiff > 0) {
            dispatch(change(FORM_NAME, "txtDays", daysDiff > 999 ? '999+' : daysDiff));
        } else {
            dispatch(change(FORM_NAME, "txtDays", null));
            dispatch(untouch(FORM_NAME, "txtDays"));
        }
    }, [dispatch, syncDateComment]);

    const onTimeZoneChange = useCallback(() => {
        if (isEdit) dispatch(change(FORM_NAME, "isDateComment", true));
    }, [isEdit, dispatch]);

    const onReminderToggle = useCallback((_, value) => {
        dispatch(change(FORM_NAME, "reminder_setting_iso", value ? DEFAULT_REMINDER_TIME() : null));
        if (!value) dispatch(untouch(FORM_NAME, "reminder_setting_iso"));
    }, [dispatch]);

    const handleCalendarTimeSelect = useCallback((dateTime) => {
        dispatch(change(FORM_NAME, "DateTime", dateTime));
        onDateTimeChange(null, dateTime);
        dispatch(hideTooltip());
    }, [dispatch, onDateTimeChange]);

    const showMyCalendar = useCallback((e) => {
        if (userId !== ownerId) return;
        dispatch(showTooltip({
            target: e.target.getBoundingClientRect(),
            hideOnDocClick: true,
            showOnClick: true,
            position: !isMobile() && "right",
            toolTipClassName: 'diaryCalendar',
            childComponent: (
                <MyCalendar
                    hideCalendar={() => dispatch(hideTooltip())}
                    selectedDate={dueDate}
                    onTimeSelect={handleCalendarTimeSelect}
                />
            ),
        }));
    }, [userId, ownerId, dueDate, dispatch, handleCalendarTimeSelect]);

    // Confirmation handler
    const handleConfirmCancel = useCallback(() => {
        closeForm();
    }, [closeForm]);

    const handleDismissModal = useCallback(() => {
        setModalSize("medium");
        setShowModal(false);
        toggleFullView(true);
    }, [toggleFullView]);

    if (currentPage !== "form") return null;

    return (
        <>
            {showModal ? (
                <CancelConfirmModal
                    onConfirm={handleConfirmCancel}
                    onCancel={handleDismissModal}
                />
            ) : (
                <>
                    <div className="modalHeader alignMiddle">
                        <h1>{isEdit ? "Edit Diary" : "Create a new Diary"}</h1>
                        <button type="button" className="btn-cc btn-cc__transparent" onClick={handleCancelClick}>
                            <Icon name="close" className="icon ml" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit((values) => onSubmit(submitType, values))}>
                        <div className="modalBody">
                            <div className="creationForm diaryCreation">
                                <Field
                                    component={FormTextInput}
                                    wrapperClass="wth-8"
                                    className="wth-4"
                                    name="title"
                                    type="text"
                                    label="Title"
                                    isRequired
                                    helpText="Please provide a single line of text about this Diary so that you may use it as a reference."
                                    autoFocus
                                />
                                <Field
                                    component={FormTextEditor}
                                    name="description"
                                    wrapperClass="colFlex"
                                    type="text"
                                    label="Description"
                                    helpText="Write a few lines about this Diary. Please make sure it provides good insight about the Diary for future reference."
                                />

                                {/* ── Due Date ── */}
                                <div className="formGroup colFlex">
                                    <label className="rowSpaceBetween">
                                        <span>Due Date<sup>*</sup></span>
                                        {userId === ownerId && (
                                            <em className="ml">
                                                <button type="button" className="pointer ttController link-btn" onClick={showMyCalendar}>
                                                    View my calendar
                                                </button>
                                            </em>
                                        )}
                                    </label>
                                    <div className="rowFlex wth-12 alignMiddle">
                                        <div className="daysMob">
                                            <Field
                                                component={FormTextInput}
                                                name="txtDays"
                                                maxLength="4"
                                                isRequired
                                                wrapperClass="wth-1 mr mrNone"
                                                onChange={onTxtDaysChange}
                                                onFocus={onTxtDaysFocus}
                                            />
                                            <em className="mr__1 ml">days from now or</em>
                                        </div>
                                        <div className="timeCalMob">
                                            <Field
                                                component={FormDatePicker}
                                                name="due_on_iso"
                                                onChange={onDateTimeChange}
                                                shouldCloseOnSelect={false}
                                                wrapperClass="formGroup wth-4 mrNone"
                                                showTime
                                                openToDate
                                            />
                                            <div className="formGroup colFlex ml mbNone">
                                                <Field
                                                    component={FormSelect}
                                                    onChange={onTimeZoneChange}
                                                    name="due_on_tz"
                                                    isRequired
                                                    options={timezoneData}
                                                    wrapperClass="mbNone dueSelect"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isDateComment && (
                                    <Field
                                        component={FormTextInput}
                                        name="dateChangeComment"
                                        type="text"
                                        label="Leave a Comment"
                                        isRequired
                                        helpText="Please state the reason for changing the due date."
                                    />
                                )}

                                {/* ── Reminder ── */}
                                <div className="rowFlex formRow">
                                    <div className="formGroup colFlex wth-8">
                                        <div className="rowFlex">
                                            <Field name="enableReminder" id="enableReminder" onChange={onReminderToggle} component="input" type="checkbox" />
                                            <label htmlFor="enableReminder">Remind Me</label>
                                        </div>
                                        <div className="alignMiddle">
                                            <div className="selfStart wth-5">
                                                <Field
                                                    component={FormDatePicker}
                                                    name="reminder_setting_iso"
                                                    inputClass={isReminderDisabled ? 'disabled' : ''}
                                                    wrapperClass={isReminderDisabled ? 'disabled' : ''}
                                                    disabled={isReminderDisabled}
                                                    shouldCloseOnSelect={false}
                                                    showTime
                                                />
                                            </div>
                                            <Field
                                                component={FormSelect}
                                                name="reminder_setting_tz"
                                                inputClass={isReminderDisabled ? 'disabled' : ''}
                                                wrapperClass={isReminderDisabled ? 'disabled' : ''}
                                                divClass={'wth-7 mrNone'}
                                                options={timezoneData}
                                                isDisabled={isReminderDisabled}
                                            />
                                        </div>
                                    </div>
                                    <div className="priorityField">
                                        <Field
                                            component={FormSelect}
                                            name="priority"
                                            isRequired
                                            options={constant.DIARY_PRIORITY_OPTIONS}
                                            label="Priority"
                                            wrapperClass="colFlex"
                                        />
                                    </div>
                                </div>

                                <Field
                                    component={FormTags}
                                    systemTags={systemTags}
                                    name="tags"
                                    suggestions={tags}
                                    label="Tags"
                                    isRequired
                                    helpText="Start typing to create a tag or press down to select a suggested tag. Tag should have at least 3 characters."
                                />
                                <Field
                                    component={FormDiaryOwner}
                                    name="owner"
                                    label="Diary Owner"
                                    isRequired
                                    isLoading={isLoading}
                                />
                            </div>

                            <Field
                                component={FormFileUpload}
                                name="file"
                                maxFileUploadSize={MAX_UPLOAD_FILE_SIZE}
                                invalidFilesToUpload={INVALID_FILES_TO_UPLOAD}
                                fileUploadUrl={constant.FILE_UPLOAD_URL}
                                allowMultiple={false}
                                helpText="Only one file can be attached to a Diary."
                            />

                            <FormFooter
                                buttonLoading={buttonLoading}
                                isEdit={isEdit}
                                onSave={() => setSubmitType(isEdit ? "update" : "save")}
                                onFollowUp={() => setSubmitType("addFollowUp")}
                                onCancel={handleCancelClick}
                            />
                        </div>
                    </form>
                </>
            )}
        </>
    );
};

export default reduxForm({
    form: FORM_NAME,
    destroyOnUnmount: false,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    validate,
    touchOnBlur: false,
    onSubmitFail,
})(DiaryCreationForm);